import * as React from 'react';
import { FileUp, X } from 'lucide-react';
import { useDropzone, type Accept } from 'react-dropzone';
import { cn } from '@/lib/utils';

export interface DropzoneProps {
  value?: File[];
  onChange: (files: File[]) => void;
  accept?: Accept;
  multiple?: boolean;
  maxSize?: number;
  disabled?: boolean;
  label?: string;
  description?: string;
  emptyText?: string;
  className?: string;
}

function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

export function Dropzone({
  value = [],
  onChange,
  accept,
  multiple = true,
  maxSize,
  disabled = false,
  label = 'Drop files here',
  description = 'Drag and drop files here, or click to browse.',
  emptyText = 'No files selected.',
  className,
}: DropzoneProps) {
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept,
    disabled,
    maxSize,
    multiple,
    onDrop: (acceptedFiles) => {
      if (!acceptedFiles.length) return;
      onChange(multiple ? [...value, ...acceptedFiles] : acceptedFiles.slice(0, 1));
    },
  });

  function removeFile(index: number) {
    onChange(value.filter((_, currentIndex) => currentIndex !== index));
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div
        {...getRootProps({
          className: cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 px-4 py-6 text-center transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            isDragActive && 'border-primary bg-primary/10',
            disabled && 'cursor-not-allowed opacity-60',
          ),
        })}
      >
        <input {...getInputProps()} />
        <FileUp className="h-7 w-7 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-1 text-xs text-muted-foreground">{description}</p>
          {maxSize ? <p className="mt-1 text-xs text-muted-foreground">Max size: {formatFileSize(maxSize)}</p> : null}
        </div>
      </div>
      {fileRejections.length ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
          {fileRejections.map((rejection) => (
            <p key={`${rejection.file.name}-${rejection.file.size}`}>
              {rejection.file.name}: {rejection.errors.map((error) => error.message).join(', ')}
            </p>
          ))}
        </div>
      ) : null}
      {value.length ? (
        <ul className="space-y-2">
          {value.map((file, index) => (
            <li key={`${file.name}-${file.size}-${file.lastModified}`} className="flex items-center justify-between gap-3 rounded-md border bg-background px-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="block truncate font-medium">{file.name}</span>
                <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
              </span>
              <button
                type="button"
                className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={(event) => {
                  event.stopPropagation();
                  removeFile(index);
                }}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">{emptyText}</p>
      )}
    </div>
  );
}
